import '../css/app.css';

// Auto-hosted fonts for Finzzo V2 (PRD-04) — imported from JS so Vite emits the woff files
import '@fontsource/geist-mono/400.css';
import '@fontsource/geist-mono/500.css';
import '@fontsource/geist/400.css';
import '@fontsource/geist/500.css';
import '@fontsource/geist/600.css';
import '@fontsource/geist/700.css';
import '@fontsource/newsreader/400.css';
import '@fontsource/newsreader/500.css';
import '@fontsource/newsreader/600.css';
import '@fontsource/newsreader/700.css';

import AiReportGenerationTracker from '@/Components/AiReportGenerationTracker.vue';
import ImpersonationBanner from '@/Components/ImpersonationBanner.vue';
import ToastNotifications from '@/Components/Ui/ToastNotifications.vue';
import { registerBackgroundRequests } from '@/composables/backgroundRequests';
import { registerIdentityGeneration } from '@/composables/identityGeneration';
import {
    HYDRATING_THEME,
    initializeHydratedTheme,
    useTheme,
} from '@/composables/useTheme';
import { createInertiaApp, usePage } from '@inertiajs/vue3';
import dayjs from 'dayjs';
import 'dayjs/locale/en';
import 'dayjs/locale/fr';
import { resolvePageComponent } from 'laravel-vite-plugin/inertia-helpers';
import { i18nVue, loadLanguageAsync } from 'laravel-vue-i18n';
import { createPinia } from 'pinia';
import { createApp, createSSRApp, DefineComponent, h, watch } from 'vue';

// PRD-04: Reka UI (headless primitives for new design system)
// No global plugin/config needed - import components on-demand e.g. from 'reka-ui'
// e.g. import * as Dialog from 'reka-ui' or named: { Root as DialogRoot } ...
// PrimeVue fully removed in PRD-04 (direct imports, v-tooltip replaced by Ui/Tooltip, config and directive purged).

registerBackgroundRequests();

let appName = import.meta.env.VITE_APP_NAME || 'Finzzo';

createInertiaApp({
    title: (title) => `${title} - ${appName}`,
    resolve: async (name) => {
        if (name !== 'Public/Landing') await import('./bootstrap');
        return resolvePageComponent(
            `./Pages/${name}.vue`,
            import.meta.glob<DefineComponent>('./Pages/**/*.vue'),
        );
    },
    async setup({
        el,
        App,
        props,
        plugin,
    }: {
        el: any;
        App: any;
        props: any;
        plugin: any;
    }) {
        registerIdentityGeneration(
            props.initialPage.props.auth?.identity_generation ?? '',
        );
        // Locale dayjs aligné sur la langue de l'application (fr par défaut) pour les
        // libellés de mois/jours en français (charts, dates relatives, etc.)
        appName = props.initialPage?.props?.app?.name || appName;
        const appLang: string = props.initialPage?.props?.app?.lang || 'fr';
        dayjs.locale(appLang === 'en' ? 'en' : 'fr');

        const hydrating = el.hasAttribute('data-server-rendered');
        if (hydrating) {
            initializeHydratedTheme(
                props.initialPage.props.auth?.user?.preferences?.theme ||
                    'system',
            );
        }
        const app = (hydrating ? createSSRApp : createApp)({
            setup() {
                useTheme();
                const page = usePage();
                watch(
                    () => page.props?.app?.lang,
                    async (lang) => {
                        if (!lang) return;
                        await loadLanguageAsync(lang);
                        dayjs.locale(lang === 'en' ? 'en' : 'fr');
                        document.documentElement.lang = lang;
                    },
                );
                // Toasts montés une seule fois à la racine : les layouts sont des
                // wrappers remontés à chaque visite, ils perdraient l'événement flash.
                return () => [
                    h(ImpersonationBanner),
                    h(App, props),
                    h(ToastNotifications),
                    h(AiReportGenerationTracker),
                ];
            },
            provide: {
                dayjs: dayjs,
                uc_first: (string: string): string =>
                    string.charAt(0).toUpperCase() + string.slice(1),
                pluralize: (word: string, amount: number): string =>
                    amount > 1 || amount === 0 ? `${word}s` : word,
                slugify: (string: string, separator: string = '-'): string => {
                    return string
                        .toString() // Cast to string (optional)
                        .normalize('NFKD') // The normalize() using NFKD method returns the Unicode Normalization Form of a given string.
                        .toLowerCase() // Convert the string to lowercase letters
                        .trim() // Remove whitespace from both sides of a string (optional)
                        .replace(/\s+/g, separator) // Replace spaces with -
                        .replace(/[^\w-]+/g, '') // Remove all non-word chars
                        .replace(/--+/g, separator); // Replace multiple - with single -
                },
                str_limit: (string: string, limit: number = 30): string => {
                    if (string.length < limit) {
                        return string;
                    }

                    return string.substring(0, limit) + '...';
                },
                format_number: (number: number): string => {
                    return number.toFixed(2);
                },
                is_mobile_view: window.matchMedia(
                    'only screen and (max-width: 600px)',
                ).matches,
            },
        })
            .provide(HYDRATING_THEME, hydrating)
            .use(plugin)
            .use(i18nVue, {
                resolve: async (lang) => {
                    const langs = import.meta.glob<{
                        default: Record<string, string>;
                    }>('../../lang/*.json');
                    return await langs[`../../lang/${lang}.json`]();
                },
            })
            .use(createPinia());

        // Mount once the translated copy is available, so headings do not shift from keys to text.
        await loadLanguageAsync(appLang);
        app.mount(el);
    },
    progress: {
        color: '#4B5563',
    },
});
